import {
  AnalyzeResultFragment,
  AnalyzeResultProcessType,
} from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatInteger } from '../../common/formatHelper';
import useFormatter from '../../common/useFormatter';
import { cn } from '../../common/utils';
import { AgentCategoryId } from '../../resourcePolicy/agentData/agent.types';
import { getAgentIcon } from '../../resourcePolicy/agentData/agentDataHelper';
import OptimisationEventsComparePanel from '../../simulation/panels/OptimisationEventsComparePanel';
import {
  getAnalyzeTotals,
  getCarbonValue,
} from '../../simulation/store/analyze.helper';
import InboxZero from '../InboxZero';
import TitleSection from '../layout/TitleSection';
import { Stat } from './Stat';
import { StatGroup } from './StatGroup';

interface StatsComparePanelProps {
  analyzeBefore?: AnalyzeResultFragment;
  analyzeAfter?: AnalyzeResultFragment;
}

const StatsCompareSummaryPanel: React.FC<StatsComparePanelProps> = ({
  analyzeBefore,
  analyzeAfter,
}) => {
  const { t } = useTranslation('app');
  const formatter = useFormatter();

  if (!analyzeBefore && !analyzeAfter) {
    return (
      <StatGroup
        title={t`Optimisation Summary`}
        subTitle={t`Stats`}
        helpNavTo={'simulation/optimise/simulation-optimise-summary-diff'}
      >
        <InboxZero selfCenter message={t`No Analysed Data`} />
      </StatGroup>
    );
  } else if (!analyzeBefore) {
    return (
      <StatGroup
        title={t`Optimisation Summary`}
        subTitle={t`Stats`}
        helpNavTo={'simulation/optimise/simulation-optimise-summary-diff'}
      >
        <InboxZero selfCenter message={t`No Analyse Data (First)`} />
      </StatGroup>
    );
  } else if (!analyzeAfter) {
    return (
      <StatGroup
        title={t`Optimisation Summary`}
        subTitle={t`Stats`}
        helpNavTo={'simulation/optimise/simulation-optimise-summary-diff'}
      >
        <InboxZero selfCenter message={t`No Analyse Data (Second)`} />
      </StatGroup>
    );
  }

  const totalsBefore = getAnalyzeTotals(analyzeBefore);
  const totalsAfter = getAnalyzeTotals(analyzeAfter);

  console.debug('analyzeBefore', analyzeBefore);
  console.debug('analyzeAfter', analyzeAfter);

  // Cost Data
  const costBefore = formatter.formatCurrency(totalsBefore.cost);
  const costAfter = formatter.formatCurrency(totalsAfter.cost);
  const costDelta = formatter.formatCurrency(costBefore.raw - costAfter.raw, 1);
  const costShareNumeric = 1 - totalsAfter.cost / totalsBefore.cost;
  const costShare = formatter.formatShare(costShareNumeric);

  // Distance Data
  const distanceBefore = formatter.formatDistance(totalsBefore.distance);
  const distanceAfter = formatter.formatDistance(totalsAfter.distance);
  const distanceDelta = formatter.formatDistance(
    distanceBefore.raw - distanceAfter.raw,
    1,
  );
  const distanceShareNumeric = 1 - distanceAfter.raw / distanceBefore.raw;
  const distanceShare = formatter.formatShare(distanceShareNumeric);

  // Duration Data
  const durationBefore = formatter.formatTimeSpan(totalsBefore.duration, 1);
  const durationAfter = formatter.formatTimeSpan(totalsAfter.duration, 1);
  const durationDelta = formatter.formatTimeSpan(
    durationBefore.raw - durationAfter.raw,
  );
  const durationShareNumeric = 1 - totalsAfter.duration / totalsBefore.duration;
  const durationShare = formatter.formatShare(durationShareNumeric);

  // CO2 Data
  const carbonBeforeValue = getCarbonValue(analyzeBefore);
  const carbonAfterValue = getCarbonValue(analyzeAfter);
  const carbonBefore = formatter.formatCO2(carbonBeforeValue);
  const carbonAfter = formatter.formatCO2(carbonAfterValue);
  const carbonDelta = formatter.formatCO2(carbonBefore.raw - carbonAfter.raw);
  const carbonShareNumeric = 1 - carbonAfter.raw / carbonBefore.raw;
  const carbonShare = formatter.formatShare(carbonShareNumeric);

  // Order Pickability (Count of Assembled Orders)
  const orderCountAssembledBefore = formatter.formatInteger(
    analyzeBefore.totalAssembledOrders +
      analyzeBefore.totalAssembledPartiallyOrders,
  );
  const orderountAssembledAfter = formatter.formatInteger(
    analyzeAfter.totalAssembledOrders +
      analyzeAfter.totalAssembledPartiallyOrders,
  );

  // Order Lines
  const totalOrderLinesBeforeRaw =
    analyzeBefore?.assembledLines +
    analyzeBefore?.assembledPartiallyLines +
    analyzeBefore?.ignoredLines;

  const totalOrderLinesBeforeCount = formatter.formatInteger(
    totalOrderLinesBeforeRaw,
  );

  const totalOrderLinesAfterRaw =
    analyzeAfter?.assembledLines +
    analyzeAfter?.assembledPartiallyLines +
    analyzeAfter?.ignoredLines;

  const totalOrderLinesAfterCount = formatter.formatInteger(
    totalOrderLinesAfterRaw,
  );

  const assembledLinesBeforeRaw = analyzeBefore?.assembledLines ?? 0;
  const assembledLinesAfterRaw = analyzeAfter?.assembledLines ?? 0;
  const assembledLinesBeforeCount = formatter.formatInteger(
    assembledLinesBeforeRaw,
  );
  const assembledLinesAfterCount = formatter.formatInteger(
    assembledLinesAfterRaw,
  );

  const assembledPartiallyLinesBeforeRaw =
    analyzeBefore?.assembledPartiallyLines ?? 0;
  const assembledPartiallyLinesAfterRaw =
    analyzeAfter?.assembledPartiallyLines ?? 0;
  const assembledPartiallyLinesBeforeCount = formatter.formatInteger(
    assembledPartiallyLinesBeforeRaw,
  );
  const assembledPartiallyLinesAfterCount = formatter.formatInteger(
    assembledPartiallyLinesAfterRaw,
  );

  const ignoredLinesBeforeRaw = analyzeBefore?.ignoredLines ?? 0;
  const ignoredLinesAfterRaw = analyzeAfter?.ignoredLines ?? 0;
  const ignoredLinesBeforeCount = formatter.formatInteger(
    ignoredLinesBeforeRaw,
  );
  const ignoredLinesAfterCount = formatter.formatInteger(ignoredLinesAfterRaw);

  // Performance
  const totalAssembledOrderLinesCountBeforeRaw =
    analyzeBefore.assembledLines + analyzeBefore.assembledPartiallyLines;

  const totalAssembledOrderLinesBeforeCount = formatter.formatInteger(
    totalAssembledOrderLinesCountBeforeRaw,
  );

  const totalAssembledOrderLinesCountAfterRaw =
    analyzeAfter.assembledLines + analyzeAfter.assembledPartiallyLines;

  const totalAssembledOrderLinesAfterCount = formatter.formatInteger(
    totalAssembledOrderLinesCountAfterRaw,
  );

  const pickingDurationBeforeRaw = _.find(
    analyzeBefore?.costDetails?.processes,
    p => p.processType === AnalyzeResultProcessType.PICKING,
  )?.duration;

  const pickingDurationAfterRaw = _.find(
    analyzeAfter?.costDetails?.processes,
    p => p.processType === AnalyzeResultProcessType.PICKING,
  )?.duration;

  const linesPerHourBeforeRaw =
    pickingDurationBeforeRaw > 0
      ? (totalAssembledOrderLinesCountBeforeRaw * 3600) /
        pickingDurationBeforeRaw
      : 0;

  const linesPerHourBefore = formatter.formatToPrecision(
    linesPerHourBeforeRaw,
    2,
    t`lines/h`,
  );

  const linesPerHourAfterRaw =
    pickingDurationAfterRaw > 0
      ? (totalAssembledOrderLinesCountAfterRaw * 3600) / pickingDurationAfterRaw
      : 0;
  const linesPerHourAfter = formatter.formatToPrecision(
    linesPerHourAfterRaw,
    2,
    t`lines/h`,
  );

  const linesPerHourShare = formatter.formatShare(
    linesPerHourBeforeRaw > 0
      ? linesPerHourAfterRaw / linesPerHourBeforeRaw - 1
      : 0,
  );

  const linesPerHourDelta = formatter.formatToPrecision(
    linesPerHourAfterRaw - linesPerHourBeforeRaw,
    2,
    t`lines/hour`,
  );

  const uomStatsBefore = _.keyBy(
    analyzeBefore.picklistSet?.linesSummary,
    row => row.key.uom,
  );
  const uomStatsAfter = _.keyBy(
    analyzeAfter.picklistSet?.linesSummary,
    row => row.key.uom,
  );

  const distinctUoms = _.uniq([
    ..._.keys(uomStatsBefore),
    ..._.keys(uomStatsAfter),
  ]);

  // Get all unique process types from both analyses
  const processTypesBefore =
    analyzeBefore?.costDetails?.processes?.map(p => p.processType) || [];
  const processTypesAfter =
    analyzeAfter?.costDetails?.processes?.map(p => p.processType) || [];
  const distinctProcessTypes = _.uniq([
    ...processTypesBefore,
    ...processTypesAfter,
  ]);

  // Create a lookup for quick access to process data
  const processesLookupBefore = _.keyBy(
    analyzeBefore?.costDetails?.processes || [],
    'processType',
  );
  const processesLookupAfter = _.keyBy(
    analyzeAfter?.costDetails?.processes || [],
    'processType',
  );

  // Extract agent data directly from AnalyzeResultFragment
  const agentsBefore = analyzeBefore?.summary?.agents || [];
  const agentsAfter = analyzeAfter?.summary?.agents || [];

  // Get all unique agent IDs from both analyses
  const agentIdsBefore = agentsBefore.map(a => a.agent);
  const agentIdsAfter = agentsAfter.map(a => a.agent);
  const distinctAgentIds = _.uniq([...agentIdsBefore, ...agentIdsAfter]);

  // Create agent lookups for quick access
  const agentsLookupBefore = _.keyBy(agentsBefore, 'agent');
  const agentsLookupAfter = _.keyBy(agentsAfter, 'agent');

  return (
    <>
      <StatGroup
        title={t`Optimisation Summary`}
        subTitle={t`Stats`}
        helpNavTo={'simulation/optimise/simulation-optimise-summary-diff'}
      >
        <Stat
          inPanelMode
          hasHelper
          isPreview
          title={t`Total Cost`}
          valueDiffPercent={costShare.value}
          valueDiff={costDelta.value}
          valueDiffUnitOfMeasure={costDelta.unit}
          valueBefore={costBefore}
          valueAfter={costAfter}
          isComparable
          unitOfMeasure={costAfter.unit}
          isUnitPrefix={costAfter.prefixUnit}
          valueIsBig
          isFullWidth
        />
        <Stat
          isFullWidth
          inPanelMode
          hasHelper
          isPreview
          title={t`Total Distance`}
          valueDiffPercent={distanceShare.value}
          valueDiff={distanceDelta.value}
          valueDiffUnitOfMeasure={distanceDelta.unit}
          valueBefore={distanceBefore}
          valueAfter={distanceAfter}
          isComparable
          valueIsBig
          unitOfMeasure={distanceBefore.unit}
        />
        <Stat
          inPanelMode
          hasHelper
          isPreview
          title={t`Total Time`}
          valueBefore={durationBefore}
          valueAfter={durationAfter}
          valueDiffPercent={durationShare.value}
          valueDiff={durationDelta.value}
          valueDiffUnitOfMeasure={durationDelta.unit}
          isComparable
          unitOfMeasure={durationBefore.unit}
          valueIsBig
        />
        {/* {(carbonBefore.raw > 0 || carbonAfter.raw > 0) && (
          <Stat
            inPanelMode
            hasHelper
            isPreview
            title={t`CO2`}
            valueBefore={carbonBefore}
            valueAfter={carbonAfter}
            valueDiffPercent={carbonShare.value}
            valueDiff={carbonDelta.value}
            isComparable
            unitOfMeasure={carbonBefore.unit}
            valueIsBig
          />
        )} */}

        {(linesPerHourBeforeRaw > 0 || linesPerHourAfterRaw > 0) && (
          <Stat
            inPanelMode
            hasHelper
            isPreview
            title={t`Performance (Lines/hour)`}
            valueBefore={linesPerHourBefore}
            valueAfter={linesPerHourAfter}
            valueDiffPercent={linesPerHourShare.value}
            valueDiff={linesPerHourDelta.value}
            isComparable
            unitOfMeasure={linesPerHourBefore.unit}
            valueIsBig
          />
        )}

        {distinctUoms.length <= 3 ? (
          // Display normally when 3 or fewer UOMs
          _.map(distinctUoms, uom => {
            const uphBefore =
              ((uomStatsBefore[uom]?.uomQuantity ?? 0) * 3600) /
              pickingDurationBeforeRaw;
            const uphAfter =
              ((uomStatsAfter[uom]?.uomQuantity ?? 0) * 3600) /
              pickingDurationAfterRaw;

            if (uphBefore > 0 || uphAfter > 0) {
              return (
                <Stat
                  title={`UPH: ${uom} / hour`}
                  valueBefore={formatter.formatInteger(uphBefore)}
                  valueAfter={formatter.formatInteger(uphAfter)}
                  isComparable
                  valueDiff={formatInteger(uphAfter - uphBefore)}
                  valueDiffPercent={
                    formatter.formatShare(uphAfter / uphBefore - 1).value
                  }
                  inPanelMode
                  unitOfMeasure={`${uom}/h`}
                  valueIsBig
                />
              );
            }
            return null;
          })
        ) : (
          // Use collapsible section when more than 3 UOMs
          <TitleSection
            key={`dataset-metric-group-uph`}
            id={`dataset-metric-group-uph`}
            title={t`Units Per Hour (UPH)`}
            inPanelView
            collapsible
            classNameInner="space-y-1"
          >
            {_.map(distinctUoms, uom => {
              const uphBefore =
                ((uomStatsBefore[uom]?.uomQuantity ?? 0) * 3600) /
                pickingDurationBeforeRaw;
              const uphAfter =
                ((uomStatsAfter[uom]?.uomQuantity ?? 0) * 3600) /
                pickingDurationAfterRaw;

              if (uphBefore > 0 || uphAfter > 0) {
                return (
                  <Stat
                    title={`UPH: ${uom} / hour`}
                    valueBefore={formatter.formatInteger(uphBefore)}
                    valueAfter={formatter.formatInteger(uphAfter)}
                    isComparable
                    valueDiff={formatInteger(uphAfter - uphBefore)}
                    valueDiffPercent={
                      formatter.formatShare(uphAfter / uphBefore - 1).value
                    }
                    inPanelMode
                    unitOfMeasure={`${uom}/h`}
                    valueIsBig
                  />
                );
              }
              return null;
            })}
          </TitleSection>
        )}

        {distinctUoms.length <= 3 ? (
          // Display normally when 3 or fewer UOMs
          _.map(distinctUoms, uom => {
            const uomCountBefore = uomStatsBefore[uom]?.count;
            const uomCountAfter = uomStatsAfter[uom]?.count;

            if (uomCountBefore > 0 || uomCountAfter > 0) {
              return (
                <Stat
                  title={`Picklists: ${uom}`}
                  valueBefore={formatter.formatInteger(uomCountBefore)}
                  valueAfter={formatter.formatInteger(uomCountAfter)}
                  isComparable
                  valueDiff={formatInteger(uomCountAfter - uomCountBefore)}
                  valueDiffPercent={
                    formatter.formatShare(uomCountBefore / uomCountAfter - 1)
                      .value
                  }
                  inPanelMode
                  valueIsBig
                />
              );
            }
            return null;
          })
        ) : (
          // Use collapsible section when more than 3 UOMs
          <TitleSection
            key={`dataset-metric-group-picklists`}
            id={`dataset-metric-group-picklists`}
            title={t`Picklists by UOM`}
            inPanelView
            collapsible
            classNameInner="space-y-1"
          >
            {_.map(distinctUoms, uom => {
              const uomCountBefore = uomStatsBefore[uom]?.count;
              const uomCountAfter = uomStatsAfter[uom]?.count;

              if (uomCountBefore > 0 || uomCountAfter > 0) {
                return (
                  <Stat
                    title={`Picklists: ${uom}`}
                    valueBefore={formatter.formatInteger(uomCountBefore)}
                    valueAfter={formatter.formatInteger(uomCountAfter)}
                    isComparable
                    valueDiff={formatInteger(uomCountAfter - uomCountBefore)}
                    valueDiffPercent={
                      formatter.formatShare(uomCountBefore / uomCountAfter - 1)
                        .value
                    }
                    inPanelMode
                    valueIsBig
                  />
                );
              }
              return null;
            })}
          </TitleSection>
        )}

        <TitleSection
          key={`dataset-metric-group-uom`}
          id={`dataset-metric-group-uom`}
          title={t`Units Metrics`}
          inPanelView
          collapsible
          classNameInner="space-y-1"
        >
          {_.map(distinctUoms, uom => {
            const netWeightCountBefore = formatter.toPrecision(
              uomStatsBefore[uom]?.netWeight,
              2,
            );

            const netWeightCountAfter = formatter.toPrecision(
              uomStatsAfter[uom]?.netWeight,
              2,
            );

            if (netWeightCountBefore > 0 || netWeightCountAfter > 0) {
              return (
                <Stat
                  title={`Weight (Net): ${uom}`}
                  valueBefore={formatter.formatWeight(netWeightCountBefore)}
                  valueAfter={formatter.formatWeight(netWeightCountAfter)}
                  isComparable
                  valueDiff={
                    formatter.formatWeight(
                      netWeightCountAfter - netWeightCountBefore,
                    ).fullString
                  }
                  valueDiffPercent={
                    formatter.formatShare(
                      netWeightCountBefore / netWeightCountAfter - 1,
                    ).value
                  }
                  inPanelMode
                  valueIsBig
                />
              );
            }
            return null;
          })}

          {_.map(distinctUoms, uom => {
            const volumeCountBefore = uomStatsBefore[uom]?.volume;

            const volumeCountAfter = uomStatsAfter[uom]?.volume;

            if (volumeCountBefore > 0 || volumeCountAfter > 0) {
              return (
                <Stat
                  title={`Volume: ${uom}`}
                  valueBefore={formatter.formatVolume(volumeCountBefore)}
                  valueAfter={formatter.formatVolume(volumeCountAfter)}
                  isComparable
                  valueDiff={
                    formatter.formatVolume(volumeCountAfter - volumeCountBefore)
                      .fullString
                  }
                  valueDiffPercent={
                    formatter.formatShare(
                      volumeCountBefore / volumeCountAfter - 1,
                    ).value
                  }
                  inPanelMode
                  valueIsBig
                />
              );
            }
            return null;
          })}
          {_.map(distinctUoms, uom => {
            const uomQtyBefore = uomStatsBefore[uom]?.uomQuantity ?? 0;
            const uomQtyAfter = uomStatsAfter[uom]?.uomQuantity ?? 0;
            return (
              <Stat
                title={`UOM: ${uom}`}
                valueBefore={formatter.formatInteger(uomQtyBefore)}
                valueAfter={formatter.formatInteger(uomQtyAfter)}
                isComparable
                valueDiff={formatInteger(uomQtyAfter - uomQtyBefore)}
                inPanelMode
                valueIsBig
              />
            );
          })}
        </TitleSection>
        <TitleSection
          key={`dataset-metric-group-Pickability`}
          id={`dataset-metric-group-Pickability`}
          title={t`Pickability (Order Lines)`}
          inPanelView
          collapsible
          classNameInner="space-y-1"
        >
          {orderCountAssembledBefore.raw !== orderountAssembledAfter.raw && (
            <Stat
              inPanelMode
              hasHelper
              isPreview
              title={t`Order Pickability`}
              valueBefore={orderCountAssembledBefore}
              valueAfter={orderountAssembledAfter}
              // valueDiffPercent={durationShare.value}
              // valueDiff={durationDelta}
              isComparable
              unitOfMeasure={orderCountAssembledBefore.unit}
              valueIsBig
            />
          )}
          {(totalOrderLinesBeforeRaw > 0 || totalOrderLinesAfterRaw > 0) && (
            <Stat
              title={t`Total Order Lines`}
              valueBefore={totalOrderLinesBeforeCount}
              valueAfter={totalOrderLinesAfterCount}
              isComparable
              valueDiff={formatInteger(
                totalOrderLinesAfterRaw - totalOrderLinesBeforeRaw,
              )}
              inPanelMode
            />
          )}

          {(totalAssembledOrderLinesCountBeforeRaw > 0 ||
            totalAssembledOrderLinesCountAfterRaw > 0) && (
            <Stat
              inPanelMode
              title={t`Total Picked Lines (Picked and Partial)`}
              valueBefore={totalAssembledOrderLinesBeforeCount}
              valueAfter={totalAssembledOrderLinesAfterCount}
              valueDiffPercent={
                formatter.formatShare(
                  totalAssembledOrderLinesCountAfterRaw /
                    totalAssembledOrderLinesCountBeforeRaw -
                    1,
                ).value
              }
              valueDiff={formatInteger(
                totalAssembledOrderLinesCountAfterRaw -
                  totalAssembledOrderLinesCountBeforeRaw,
              )}
              isComparable
              unitOfMeasure={totalAssembledOrderLinesBeforeCount.unit}
            />
          )}

          {(assembledLinesBeforeRaw > 0 || assembledLinesAfterRaw > 0) && (
            <Stat
              title={t`Picked Lines`}
              valueBefore={assembledLinesBeforeCount}
              valueAfter={assembledLinesAfterCount}
              isComparable
              valueDiff={formatInteger(
                assembledLinesAfterCount.raw - assembledLinesBeforeCount.raw,
              )}
              valueDiffPercent={
                formatter.formatShare(
                  assembledLinesAfterCount.raw / assembledLinesBeforeCount.raw -
                    1,
                ).value
              }
              inPanelMode
            />
          )}

          {(assembledPartiallyLinesBeforeRaw > 0 ||
            assembledPartiallyLinesAfterRaw > 0) && (
            <Stat
              title={t`Partially Picked Lines`}
              valueBefore={assembledPartiallyLinesBeforeCount}
              valueAfter={assembledPartiallyLinesAfterCount}
              isComparable
              valueDiff={formatInteger(
                assembledPartiallyLinesAfterCount.raw -
                  assembledPartiallyLinesBeforeCount.raw,
              )}
              inPanelMode
            />
          )}

          {(ignoredLinesBeforeRaw > 0 || ignoredLinesAfterRaw > 0) && (
            <Stat
              title={t`Ignored Lines`}
              valueBefore={ignoredLinesBeforeCount}
              valueAfter={ignoredLinesAfterCount}
              isComparable
              valueDiff={formatInteger(
                ignoredLinesAfterCount.raw - ignoredLinesBeforeCount.raw,
              )}
              inPanelMode
            />
          )}
        </TitleSection>
      </StatGroup>

      <TitleSection
        key={`dataset-metric-group-ProcessDetails`}
        id={`dataset-metric-group-ProcessDetails`}
        title={t`Process Details`}
        inPanelView
        collapsible
        classNameInner="space-y-1"
      >
        {distinctProcessTypes.map(processType => {
          const processBefore = processesLookupBefore[processType];
          const processAfter = processesLookupAfter[processType];

          if (!processBefore && !processAfter) return null;

          // Format process type for display (e.g., "PICKING" to "Picking")
          const formattedType =
            processType.charAt(0) + processType.slice(1).toLowerCase();

          return (
            <React.Fragment key={`process-${processType}`}>
              {processBefore?.distance !== undefined &&
                processAfter?.distance !== undefined && (
                  <Stat
                    title={t('{{context}} Distance', {
                      context: formattedType,
                    })}
                    valueBefore={formatter.formatDistance(
                      processBefore.distance,
                    )}
                    valueAfter={formatter.formatDistance(processAfter.distance)}
                    isComparable
                    valueDiff={
                      formatter.formatDistance(
                        processBefore.distance - processAfter.distance,
                      ).value
                    }
                    valueDiffUnitOfMeasure={
                      formatter.formatDistance(
                        processBefore.distance - processAfter.distance,
                      ).unit
                    }
                    valueDiffPercent={
                      formatter.formatShare(
                        1 - processAfter.distance / processBefore.distance,
                      ).value
                    }
                    inPanelMode
                  />
                )}

              {processBefore?.duration !== undefined &&
                processAfter?.duration !== undefined && (
                  <Stat
                    title={t('{{context}} Duration', {
                      context: formattedType,
                    })}
                    valueBefore={formatter.formatTimeSpan(
                      processBefore.duration,
                    )}
                    valueAfter={formatter.formatTimeSpan(processAfter.duration)}
                    isComparable
                    valueDiff={
                      formatter.formatTimeSpan(
                        processBefore.duration - processAfter.duration,
                      ).value
                    }
                    valueDiffUnitOfMeasure={
                      formatter.formatTimeSpan(
                        processBefore.duration - processAfter.duration,
                      ).unit
                    }
                    valueDiffPercent={
                      formatter.formatShare(
                        1 - processAfter.duration / processBefore.duration,
                      ).value
                    }
                    inPanelMode
                  />
                )}

              {processBefore?.cost !== undefined &&
                processAfter?.cost !== undefined && (
                  <Stat
                    title={t('{{context}} Cost', { context: formattedType })}
                    valueBefore={formatter.formatCurrency(processBefore.cost)}
                    valueAfter={formatter.formatCurrency(processAfter.cost)}
                    isComparable
                    valueDiffPercent={
                      formatter.formatShare(
                        1 - processAfter.cost / processBefore.cost,
                      ).value
                    }
                    valueDiff={
                      formatter.formatCurrency(
                        processBefore.cost - processAfter.cost,
                      ).value
                    }
                    valueDiffUnitOfMeasure={
                      formatter.formatCurrency(
                        processBefore.cost - processAfter.cost,
                      ).unit
                    }
                    inPanelMode
                  />
                )}
            </React.Fragment>
          );
        })}
      </TitleSection>

      <TitleSection
        key={`dataset-metric-group-Picklists`}
        id={`dataset-metric-group-Picklists`}
        title={t`Picklist Metrics`}
        inPanelView
        collapsible
        classNameInner="space-y-1"
      >
        {analyzeBefore?.generatedPicklistSet?.picklists?.totalCount ||
        analyzeAfter?.generatedPicklistSet?.picklists?.totalCount ? (
          <Stat
            title={t`Generated Picklists`}
            valueBefore={formatter.formatInteger(
              analyzeBefore?.generatedPicklistSet?.picklists?.totalCount ?? 0,
            )}
            valueAfter={formatter.formatInteger(
              analyzeAfter?.generatedPicklistSet?.picklists?.totalCount ?? 0,
            )}
            isComparable
            valueDiff={formatInteger(
              (analyzeAfter?.generatedPicklistSet?.picklists?.totalCount ?? 0) -
                (analyzeBefore?.generatedPicklistSet?.picklists?.totalCount ??
                  0),
            )}
            inPanelMode
          />
        ) : null}

        {analyzeBefore?.batchedPicklistSet?.picklists?.totalCount ||
        analyzeAfter?.batchedPicklistSet?.picklists?.totalCount ? (
          <Stat
            title={t`Batched Picklists`}
            valueBefore={formatter.formatInteger(
              analyzeBefore?.batchedPicklistSet?.picklists?.totalCount ?? 0,
            )}
            valueAfter={formatter.formatInteger(
              analyzeAfter?.batchedPicklistSet?.picklists?.totalCount ?? 0,
            )}
            isComparable
            valueDiff={formatInteger(
              (analyzeAfter?.batchedPicklistSet?.picklists?.totalCount ?? 0) -
                (analyzeBefore?.batchedPicklistSet?.picklists?.totalCount ?? 0),
            )}
            inPanelMode
          />
        ) : null}
      </TitleSection>

      <TitleSection
        key={`dataset-metric-group-Orders`}
        id={`dataset-metric-group-Orders`}
        title={t`Order Metrics`}
        inPanelView
        collapsible
        classNameInner="space-y-1"
      >
        <Stat
          title={t`Total Orders`}
          valueBefore={formatter.formatInteger(analyzeBefore?.totalOrders ?? 0)}
          valueAfter={formatter.formatInteger(analyzeAfter?.totalOrders ?? 0)}
          isComparable
          valueDiff={formatInteger(
            (analyzeAfter?.totalOrders ?? 0) -
              (analyzeBefore?.totalOrders ?? 0),
          )}
          inPanelMode
        />

        <Stat
          title={t`Fully Picked Orders`}
          valueBefore={formatter.formatInteger(
            analyzeBefore?.totalAssembledOrders ?? 0,
          )}
          valueAfter={formatter.formatInteger(
            analyzeAfter?.totalAssembledOrders ?? 0,
          )}
          isComparable
          valueDiff={formatInteger(
            (analyzeAfter?.totalAssembledOrders ?? 0) -
              (analyzeBefore?.totalAssembledOrders ?? 0),
          )}
          inPanelMode
        />

        <Stat
          title={t`Partially Picked Orders`}
          valueBefore={formatter.formatInteger(
            analyzeBefore?.totalAssembledPartiallyOrders ?? 0,
          )}
          valueAfter={formatter.formatInteger(
            analyzeAfter?.totalAssembledPartiallyOrders ?? 0,
          )}
          isComparable
          valueDiff={formatInteger(
            (analyzeAfter?.totalAssembledPartiallyOrders ?? 0) -
              (analyzeBefore?.totalAssembledPartiallyOrders ?? 0),
          )}
          inPanelMode
        />
      </TitleSection>

      <TitleSection
        key={`dataset-metric-group-AgentDetails`}
        id={`dataset-metric-group-AgentDetails`}
        title={t`Resource Metrics`}
        inPanelView
        collapsible
        classNameInner="space-y-1"
      >
        {distinctAgentIds.map(agentId => {
          const agentBefore = agentsLookupBefore[agentId];
          const agentAfter = agentsLookupAfter[agentId];

          if (!agentBefore && !agentAfter) return null;

          // Find agent info in resource policy
          const agentInfo =
            analyzeBefore?.resourcePolicy?.agents?.find(
              a => a.id === agentId,
            ) ||
            analyzeAfter?.resourcePolicy?.agents?.find(a => a.id === agentId);

          // Get agent title and icon
          const agentType = agentInfo?.resourceTypeId || 'UNDEFINED';
          const agentTypeTitle = agentInfo?.title;
          const agentIcon = agentInfo?.resourceTypeId
            ? getAgentIcon(agentInfo?.resourceTypeId as AgentCategoryId)
            : null;
          const AgentIcon = agentInfo?.resourceTypeId
            ? getAgentIcon(agentInfo.resourceTypeId as AgentCategoryId)
            : null;

          // Get all event types by process type for this agent
          const eventTypesByProcessBefore = _.groupBy(
            agentBefore?.eventTypes || [],
            'processType',
          );

          const eventTypesByProcessAfter = _.groupBy(
            agentAfter?.eventTypes || [],
            'processType',
          );

          // Get all unique process types this agent is involved in
          const processTypesBefore = Object.keys(eventTypesByProcessBefore);
          const processTypesAfter = Object.keys(eventTypesByProcessAfter);
          const agentProcessTypes = _.uniq([
            ...processTypesBefore,
            ...processTypesAfter,
          ]);

          return (
            <TitleSection
              key={`agent-${agentId}`}
              id={`agent-${agentId}`}
              title={
                <div className="flex items-center">
                  <AgentIcon
                    data-component="AgentResourceIcon"
                    className={cn(
                      'h-6 w-6',
                      'fill-current',
                      'ltr:mr-3 rtl:ml-3',
                    )}
                  />

                  {t('Agent {{context}}', { context: agentInfo?.title })}
                </div>
              }
              inPanelView
              collapsible
              classNameInner="space-y-1"
            >
              {agentProcessTypes.map(processType => {
                // Get events for this process type
                const eventsBeforeForProcess =
                  eventTypesByProcessBefore[processType] || [];
                const eventsAfterForProcess =
                  eventTypesByProcessAfter[processType] || [];

                // Format process type name
                const formattedType =
                  processType.charAt(0) + processType.slice(1).toLowerCase();

                return (
                  <React.Fragment
                    key={`agent-${agentId}-process-${processType}`}
                  >
                    <TitleSection
                      key={`agent-${agentId}-process-${processType}-events`}
                      id={`agent-${agentId}-process-${processType}-events`}
                      title={t('{{context}} Events', {
                        context: formattedType,
                      })}
                      // inTabView
                      collapsible
                      isTransparent
                      classNameTitle="text-xs"
                      className="!mb-0 border-b-0 !py-1"
                      // classNameInner="space-y-1"
                    >
                      <OptimisationEventsComparePanel
                        title={t`By event type`}
                        eventsBefore={eventsBeforeForProcess}
                        eventsAfter={eventsAfterForProcess}
                        energyRate={
                          agentInfo?.specs?.powerSource
                            ?.energyConsumptionPerSecond ?? 0
                        }
                        showKpiSwitch
                        showLegendStats
                      />
                    </TitleSection>
                  </React.Fragment>
                );
              })}
            </TitleSection>
          );
        })}
      </TitleSection>
    </>
  );
};

export default StatsCompareSummaryPanel;
